import { getAccessToken } from '../authentication/supabaseService';
import { AllergenDetailsV1, AllergenDraft } from '../../types/RecipeAllergens';
import { throwRecipeApiError } from './recipeAllergensService';

// Language codes supported
export type SupportedLanguage = 'en' | 'es' | 'nl';
export const ALL_LANGUAGES: SupportedLanguage[] = ['en', 'es', 'nl'];

// Translation object - EN is always required as fallback
export interface TranslatedText {
  en: string;       // REQUIRED - universal fallback
  es?: string;
  nl?: string;
}

export interface RecipeIngredient {
  id: string;
  nameTranslations: TranslatedText;
  amountTranslations?: TranslatedText;
}

export interface RecipeStep {
  id: string;
  titleTranslations?: TranslatedText;
  descriptionTranslations: TranslatedText;
  position?: number;
}

export interface RecipeNote {
  userId: string;
  note: string;
  createdAt: string;
}

export interface RecipeComponent {
  id: string;
  nameTranslations: TranslatedText;
  amountTranslations?: TranslatedText;
  images?: string[];
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
  notes: RecipeNote[];
  position?: number;
}

export interface Recipe {
  id: string;
  userId: string;
  restaurantId: string;
  nameTranslations: TranslatedText;
  menuItemName?: string;
  // Link to a cost price analysis. Takes priority over the legacy menuItemName
  // string join, and is the only way a custom (non-POS) dish resolves a category.
  costPriceId?: string;
  // Resolved server-side from the linked analysis' category — name + id, so the
  // badge can render without a second lookup.
  category?: string;
  categoryId?: string;
  // Manual per-restaurant order (position ASC, updated_at DESC).
  position?: number;
  images: string[];
  allergies: string[];
  serviceTypes?: ('FOOD' | 'DRINKS')[];
  allergenDetails?: AllergenDetailsV1 | null;
  diets?: string[];
  components: RecipeComponent[];
  createdAt: Date;
  updatedAt: Date;
}

export interface RecipeCreateRequest {
  userId: string;
  restaurantId: string;
  nameTranslations: TranslatedText;
  images: string[];
  allergies?: string[];
  allergenDetails?: AllergenDraft;
  diets?: string[];
  components: RecipeComponent[];
}

export interface RecipeUpdateRequest {
  id: string;
  nameTranslations?: TranslatedText;
  menuItemName?: string;
  // Cost price link. Key ABSENT = leave untouched; explicit `null` = unlink
  // (server clears cost_price_id AND the menu_item_name mirror); uuid = link.
  costPriceId?: string | null;
  images?: string[];
  allergies?: string[];
  allergenDetails?: AllergenDraft;
  expectedAllergenRevision?: string;
  diets?: string[];
  components?: RecipeComponent[];
}

// Helper to get text from TranslatedText object with fallback to EN
export function getTranslatedText(translations: TranslatedText | undefined, language: SupportedLanguage): string {
  if (!translations) return '';
  return translations[language] || translations.en || '';
}

export async function fetchRecipesByRestaurant(
  restaurantId: string
): Promise<Recipe[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/recipes/restaurant/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw Object.assign(new Error(`Failed to fetch recipes: ${res.status} ${res.statusText}`), { status: res.status });
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  }));
}

export async function fetchRecipeById(id: string): Promise<Recipe> {
  const token = await getAccessToken();
  const res = await fetch(`/api/recipes/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch recipe: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function createRecipe(
  request: RecipeCreateRequest,
  imageFiles?: File[]
): Promise<Recipe> {
  const token = await getAccessToken();

  const formData = new FormData();

  // Add recipe data as JSON string
  formData.append('recipeData', JSON.stringify(request));

  // Add image files if provided
  if (imageFiles && imageFiles.length > 0) {
    imageFiles.forEach((file) => {
      formData.append('recipeImages', file);
    });
  }

  const res = await fetch('/api/recipes/create', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  if (!res.ok) {
    await throwRecipeApiError(res);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export interface ComponentImageUpload {
  componentIndex: number;
  imageFiles: File[];
}

export async function updateRecipe(
  request: RecipeUpdateRequest,
  imageFiles?: File[],
  componentImages?: ComponentImageUpload[]
): Promise<Recipe> {
  const token = await getAccessToken();

  const formData = new FormData();

  // Add recipe data as JSON string
  formData.append('recipeData', JSON.stringify(request));

  // Add new image files if provided
  if (imageFiles && imageFiles.length > 0) {
    imageFiles.forEach((file) => {
      formData.append('recipeImages', file);
    });
  }

  // Add component images if provided
  if (componentImages && componentImages.length > 0) {
    componentImages.forEach((compImg) => {
      compImg.imageFiles.forEach((file) => {
        // Append with component index as metadata
        formData.append(`componentImages_${compImg.componentIndex}`, file);
      });
    });
  }

  const res = await fetch('/api/recipes/update', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  if (!res.ok) {
    await throwRecipeApiError(res);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

// Persist a manual recipe order for the restaurant (org-level, not per user).
export async function updateRecipePositions(
  updates: Array<{ id: string; position: number }>
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch('/api/recipes/positions/update', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ updates })
  });
  if (!res.ok) {
    throw new Error(`Failed to update recipe positions: ${res.status} ${res.statusText}`);
  }
}

export async function deleteRecipe(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/recipes/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete recipe: ${res.status} ${res.statusText}`);
  }
}

// Delete ALL recipes for a restaurant (super admin impersonation only). Returns number deleted.
export async function deleteAllRecipes(restaurantId: string): Promise<number> {
  const token = await getAccessToken();
  const res = await fetch(`/api/recipes/restaurant/${restaurantId}/all`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete all recipes: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.deletedCount ?? 0;
}

// Translate entire recipe to target languages
export async function translateRecipe(
  recipeId: string,
  sourceLanguage: SupportedLanguage,
  targetLanguages: SupportedLanguage[]
): Promise<Recipe> {
  const token = await getAccessToken();
  const res = await fetch(`/api/recipes/${recipeId}/translate-all`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ sourceLanguage, targetLanguages }),
  });
  if (!res.ok) {
    throw new Error(`Failed to translate recipe: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

// Translate individual texts
export async function translateTexts(
  texts: { key: string; value: string }[],
  sourceLanguage: SupportedLanguage,
  targetLanguages: SupportedLanguage[]
): Promise<{ translations: Record<string, Record<SupportedLanguage, string>>; cost: number }> {
  const token = await getAccessToken();
  const res = await fetch('/api/translate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ texts, sourceLanguage, targetLanguages }),
  });
  if (!res.ok) {
    throw new Error(`Failed to translate texts: ${res.status} ${res.statusText}`);
  }
  return res.json();
}
